import { useMemo, useRef, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useLocation, useParams } from 'react-router';

import { apiErrorMessage } from '../../shared/api/errors';
import { age, formatDate, fullName } from '../../shared/lib/format';
import { formatPhoneRu } from '../../shared/lib/phone';
import { useDebouncedValue } from '../../shared/lib/useDebouncedValue';
import { Button } from '../../shared/ui/Button';
import { GeneratedPasswordField } from '../../shared/ui/GeneratedPasswordField';
import { TextField, inputClass } from '../../shared/ui/TextField';
import { Card, EmptyState, ErrorText, PageHeader, Spinner } from '../../shared/ui/primitives';
import type { Role } from '../auth/api';
import { useSession } from '../auth/session';
import { listClinics } from '../clinics/api';
import { ClinicMultiSelect } from '../clinics/ClinicMultiSelect';
import { listPatients } from '../patients/api';
import {
  listDoctorsDetailed,
  resetPassword,
  setUserClinics,
  updateUser,
  type AdminDoctor,
  type CredentialsEmailStatus,
} from './api';

const ROLES: Role[] = ['doctor', 'admin'];
const roleLabel: Record<string, string> = { admin: 'Администратор', doctor: 'Врач' };

export function DoctorDetailPage() {
  const { id = '' } = useParams();
  const location = useLocation();
  const credentialsEmail = (location.state as { credentialsEmail?: CredentialsEmailStatus } | null)
    ?.credentialsEmail;
  const { user } = useSession();
  const queryClient = useQueryClient();

  const doctors = useQuery({ queryKey: ['admin', 'doctors'], queryFn: listDoctorsDetailed });
  const doctor = doctors.data?.find((d) => d.id === id);

  const [searchInput, setSearchInput] = useState('');
  const search = useDebouncedValue(searchInput.trim(), 300);
  const patients = useQuery({
    queryKey: ['patients', 'byDoctor', id, search],
    queryFn: () => listPatients({ doctorId: id, search: search || undefined }),
    enabled: Boolean(id),
  });

  return (
    <section className="space-y-6">
      <div className="space-y-2">
        <Link to="/admin/doctors" className="text-sm font-medium text-accent hover:text-accent-600">
          ← К списку врачей
        </Link>
        <PageHeader title={doctor?.fullName || doctor?.email || 'Врач'} />
      </div>

      {credentialsEmail && <CredentialsEmailNotice status={credentialsEmail} />}

      {doctors.isLoading && <Spinner />}
      {doctors.isError && (
        <ErrorText>{apiErrorMessage(doctors.error, 'Не удалось загрузить данные врача')}</ErrorText>
      )}
      {doctors.data && !doctor && <ErrorText>Врач не найден.</ErrorText>}

      {doctor && (
        <DoctorEditor
          key={doctor.id}
          value={doctor}
          selfId={user?.id}
          onSaved={() => {
            queryClient.invalidateQueries({ queryKey: ['admin'] });
            queryClient.invalidateQueries({ queryKey: ['me'] });
          }}
        />
      )}

      {doctor && <PasswordResetCard key={`pwd-${doctor.id}`} userId={doctor.id} />}

      <div className="space-y-3">
        <h2 className="text-lg font-semibold text-ink">Пациенты</h2>
        <input
          type="search"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          placeholder="Поиск по ФИО или телефону"
          className={`${inputClass} sm:max-w-xs`}
        />

        {patients.isLoading && <Spinner />}
        {patients.isError && (
          <ErrorText>{apiErrorMessage(patients.error, 'Не удалось загрузить пациентов')}</ErrorText>
        )}
        {patients.data && patients.data.items.length === 0 && (
          <EmptyState>Пациентов нет.</EmptyState>
        )}

        {patients.data && patients.data.items.length > 0 && (
          <Card className="divide-y divide-line overflow-hidden">
            {patients.data.items.map((p) => {
              const years = age(p.birthDate);
              return (
                <Link
                  key={p.id}
                  to={`/patients/${p.id}?from=/admin/doctors/${id}`}
                  className="flex flex-col gap-1 px-4 py-3 transition-colors hover:bg-canvas sm:flex-row sm:items-center sm:justify-between sm:gap-4"
                >
                  <span className="font-medium text-ink">{fullName(p)}</span>
                  <span className="flex flex-wrap gap-x-3 gap-y-0.5 text-sm text-muted">
                    <span>{formatDate(p.birthDate)}</span>
                    {years !== null && <span>{years} лет</span>}
                    {p.phone && <span>{p.phone}</span>}
                  </span>
                </Link>
              );
            })}
          </Card>
        )}

        {patients.data && <p className="text-xs text-muted">Всего: {patients.data.total}</p>}
      </div>
    </section>
  );
}

type EditValues = { fullName: string; phone: string; email: string; role: Role };

function DoctorEditor({
  value,
  selfId,
  onSaved,
}: {
  value: AdminDoctor;
  selfId?: string;
  onSaved: () => void;
}) {
  const isSelf = value.id === selfId;
  const clinicIdsRef = useRef<string[]>([]);

  const { control, register, handleSubmit } = useForm<EditValues>({
    defaultValues: {
      fullName: value.fullName,
      phone: value.phone,
      email: value.email,
      role: value.role,
    },
  });

  // API отдаёт клиники врача по именам — резолвим их в id по полному справочнику.
  const allClinics = useQuery({
    queryKey: ['clinics', 'all'],
    queryFn: () => listClinics({ limit: 500 }),
  });
  const initialRefs = useMemo(() => {
    const refs = (allClinics.data?.items ?? [])
      .filter((c) => value.clinics.includes(c.name))
      .map((c) => ({ id: c.id, name: c.name, city: c.city }));
    clinicIdsRef.current = refs.map((r) => r.id);
    return refs;
  }, [allClinics.data, value.clinics]);

  const save = useMutation({
    mutationFn: async (v: EditValues) => {
      await updateUser(value.id, v);
      if (allClinics.isSuccess) await setUserClinics(value.id, clinicIdsRef.current);
    },
    onSuccess: onSaved,
  });

  return (
    <Card className="space-y-3 p-4 sm:p-5">
      <form onSubmit={handleSubmit((v) => save.mutate(v))} className="grid gap-3 sm:grid-cols-2">
        <p className="text-sm font-semibold text-ink sm:col-span-2">Данные врача</p>
        <TextField label="ФИО" {...register('fullName')} />
        <TextField label="E-mail" type="email" {...register('email')} />
        <label className="block text-sm">
          <span className="mb-1 block font-medium text-ink/80">Телефон</span>
          <Controller
            name="phone"
            control={control}
            render={({ field: f }) => (
              <input
                className={inputClass}
                inputMode="tel"
                placeholder="+7(___)___-__-__"
                value={f.value ?? ''}
                onChange={(e) => f.onChange(formatPhoneRu(e.target.value))}
              />
            )}
          />
        </label>
        <label className="block text-sm">
          <span className="mb-1 block font-medium text-ink/80">Роль</span>
          <select className={inputClass} disabled={isSelf} {...register('role')}>
            {ROLES.map((r) => (
              <option key={r} value={r}>
                {roleLabel[r]}
              </option>
            ))}
          </select>
          {isSelf && <span className="mt-1 block text-xs text-muted">свою роль менять нельзя</span>}
        </label>

        <div className="block text-sm sm:col-span-2">
          <span className="mb-1 block font-medium text-ink/80">Клиники</span>
          <ClinicMultiSelect
            initial={initialRefs}
            onChange={(ids) => {
              clinicIdsRef.current = ids;
            }}
          />
        </div>

        <div className="flex flex-wrap items-center gap-2 sm:col-span-2">
          <Button type="submit" disabled={save.isPending}>
            Сохранить
          </Button>
          {save.isSuccess && !save.isPending && (
            <span className="text-sm text-muted">Сохранено</span>
          )}
          {save.isError && (
            <span className="text-sm text-danger">
              {apiErrorMessage(save.error, 'Не удалось сохранить')}
            </span>
          )}
        </div>
      </form>
    </Card>
  );
}

/** Сброс пароля: пароль генерируется на фронте и виден администратору для копирования. */
function PasswordResetCard({ userId }: { userId: string }) {
  const [password, setPassword] = useState('');

  const reset = useMutation({
    mutationFn: () => resetPassword(userId, password),
  });

  return (
    <Card className="space-y-3 p-4 sm:p-5">
      <p className="text-sm font-semibold text-ink">Сброс пароля</p>
      <p className="text-xs text-muted">
        Сгенерируйте новый пароль, скопируйте его и передайте врачу — все его текущие сессии будут
        завершены.
      </p>
      <GeneratedPasswordField value={password} onChange={setPassword} />
      <div className="flex flex-wrap items-center gap-2">
        <Button
          type="button"
          disabled={password.length < 8 || reset.isPending}
          onClick={() => reset.mutate()}
        >
          {reset.isPending ? 'Сохранение…' : 'Установить пароль'}
        </Button>
        {reset.isSuccess && !reset.isPending && (
          <span className="text-sm text-muted">Пароль обновлён</span>
        )}
        {reset.isError && (
          <span className="text-sm text-danger">
            {apiErrorMessage(reset.error, 'Не удалось сохранить пароль')}
          </span>
        )}
      </div>
    </Card>
  );
}

const credentialsNotice: Record<CredentialsEmailStatus, { text: string; tone: string } | null> = {
  sent: {
    text: 'Учётная запись создана, логин и пароль отправлены на e-mail.',
    tone: 'bg-accent-050 text-accent-600',
  },
  failed: {
    text: 'Учётная запись создана, но письмо с логином и паролем не отправилось. Передайте данные пользователю сами.',
    tone: 'bg-danger-050 text-danger',
  },
  disabled: {
    text: 'Учётная запись создана. Почта на сервере пока не настроена — письмо не отправлено, передайте логин и пароль пользователю сами.',
    tone: 'bg-danger-050 text-danger',
  },
  skipped: null,
};

/** Итог отправки письма с данными для входа — сразу после создания учётной записи. */
function CredentialsEmailNotice({ status }: { status: CredentialsEmailStatus }) {
  const notice = credentialsNotice[status];
  if (!notice) return null;
  return <p className={`max-w-xl rounded-lg px-3 py-2 text-sm ${notice.tone}`}>{notice.text}</p>;
}
